import Lenis from "lenis";
import gsap, { ScrollTrigger } from "./gsap";
import { handleEditor } from "@webflow/detect-editor";

type SubscriberFn = (data: any) => void;

// (*) TODO: disable on webflow editor

const SCROLL_CONFIG = {
  infinite: false,
  lerp: 1, // 1 = no smoothing, fully native feel
  smoothWheel: false,
  smoothTouch: false,
  wheelMultiplier: 1,
  touchMultiplier: 1,
  autoResize: true,
  syncTouch: false,
};

class _Scroll extends Lenis {
  #ticker = gsap.ticker.add((time) => this.raf(time * 1000));
  #resizeObserver: ResizeObserver;
  #resizeTimeout: number | null = null;

  constructor() {
    super(SCROLL_CONFIG);
    this.on("scroll", this.#scroll.bind(this));

    // Watch for body height changes and trigger Lenis resize + ScrollTrigger
    // refresh. Debounced to avoid excessive refreshes during animations.
    this.#resizeObserver = new ResizeObserver(() => {
      // Lenis resize is cheap — call immediately.
      this.resize();

      // ScrollTrigger refresh is expensive — debounce it.
      if (this.#resizeTimeout) {
        window.clearTimeout(this.#resizeTimeout);
      }
      this.#resizeTimeout = window.setTimeout(() => {
        ScrollTrigger.refresh();
        this.#resizeTimeout = null;
      }, 200);
    });
    this.#resizeObserver.observe(document.body);
  }

  #scroll(data: any) {
    // console.log("scroll", { scroll, limit, progress, velocity, time });
    this.notify(data);
  }

  toTop() {
    this.scrollTo(0, {
      immediate: true,
    });
  }

  /** Subscribable */
  #subscribers: { fn: SubscriberFn; priority: number; id: symbol }[] = [];

  add(fn: SubscriberFn, priority = 0, id = Symbol()) {
    const index = this.#subscribers.findIndex((sub) => sub.priority > priority);
    if (index === -1) {
      this.#subscribers.push({ fn, priority, id });
    } else {
      this.#subscribers.splice(index, 0, { fn, priority, id });
    }
    return () => this.remove(id);
  }

  remove(id: symbol) {
    this.#subscribers = this.#subscribers.filter((f) => f.id !== id);
  }

  notify(data: any) {
    if (this.#subscribers.length < 1) return;
    this.#subscribers.forEach((f) => f.fn(data));
  }
}

export const Scroll = new _Scroll();

handleEditor((isEditor) => {
  if (isEditor) {
    Scroll.destroy();
  } else {
    Scroll.start();
  }
});
